import {
  DEG2RAD,
  Q_EARTH_TO_WORLD,
  type Quat,
  type QuatLike,
  RAD2DEG,
  quatCopy,
  quatFromAxisAngle,
  quatMul,
  quatSlerp,
} from './math';

/** q_world = R_x(-90deg) * q_sensor (device -> WorldENU). */
export function sensorToWorld(qSensor: QuatLike, out: Quat): Quat {
  return quatMul(Q_EARTH_TO_WORLD, qSensor, out);
}

const qTmp: Quat = [0, 0, 0, 1];

/**
 * Camera quaternion in WorldENU from a (smoothed) device -> WorldENU rotation:
 *   q_cam = R_y(yawDeg) * q_world * R_z(-screenAngleDeg)
 * yawDeg = trim - declination - frameCorrection (rotates the view about the
 * vertical; positive = counter-clockwise from above, i.e. heading decreases).
 * R_z(-angle) turns the device frame into the screen frame for the current
 * screen.orientation.angle.
 */
export function composeCameraQuat(qWorld: QuatLike, yawDeg: number, screenAngleDeg: number, out: Quat): Quat {
  quatFromAxisAngle(0, 1, 0, yawDeg * DEG2RAD, qTmp);
  quatMul(qTmp, qWorld, out);
  if (screenAngleDeg !== 0) {
    quatFromAxisAngle(0, 0, 1, -screenAngleDeg * DEG2RAD, qTmp);
    quatMul(out, qTmp, out);
  }
  return out;
}

/**
 * Vertical FOV for the current viewport. `portraitVFovDeg` is the FOV along
 * the long side of the displayed camera image (vertical in portrait); in
 * landscape (aspect = width / height > 1) the vertical axis is the short side.
 * Assumes the video covers the viewport with its long side fitted.
 */
export function viewportVFovDeg(portraitVFovDeg: number, aspect: number): number {
  if (!(aspect > 1)) return portraitVFovDeg;
  const t = Math.tan((portraitVFovDeg * DEG2RAD) / 2) / aspect;
  return 2 * Math.atan(t) * RAD2DEG;
}

/** Exponential slerp smoothing of a quaternion stream (tau ~ 80 ms). */
export class QuatSmoother {
  readonly value: Quat = [0, 0, 0, 1];
  initialized = false;
  tauS: number;

  constructor(tauS = 0.08) {
    this.tauS = tauS;
  }

  reset(): void {
    this.initialized = false;
  }

  update(target: QuatLike, dtS: number): Quat {
    if (!this.initialized) {
      quatCopy(target, this.value);
      this.initialized = true;
      return this.value;
    }
    const a = dtS > 0 ? 1 - Math.exp(-dtS / this.tauS) : 0;
    if (a > 0) quatSlerp(this.value, target, a, this.value);
    return this.value;
  }
}
